import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';
import {
  ArrowLeft as ArrowLeftIcon,
  Printer as PrinterIcon,
  User as UserIcon,
  Scale as ScaleIcon,
  Ruler as RulerIcon
} from 'lucide-react';

export default function MedicoVfpHistorialPage() {
  const { clave } = useParams<{ clave: string }>();
  const navigate = useNavigate();
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [historial, setHistorial] = useState('');

  useEffect(() => {
    if (clave) {
      loadPaciente();
    }
  }, [clave]);

  const loadPaciente = async () => {
    try {
      setLoading(true);
      const data = await medicoVfpService.getPacienteVfp(parseInt(clave!));
      setPaciente(data);
      setHistorial(data.historial || '');
    } catch (error) {
      console.error('Error al cargar paciente:', error);
      toast.error('Error al cargar paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await medicoVfpService.updatePacienteVfp(parseInt(clave!), {
        historial: historial,
      });
      toast.success('Historial actualizado exitosamente');
      setEditing(false);
      loadPaciente();
    } catch (error) {
      console.error('Error al actualizar historial:', error);
      toast.error('Error al actualizar historial');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const calcularEdad = (fechaNacimiento?: Date) => {
    if (!fechaNacimiento) return '-';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }

    return `${edad} años`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!paciente) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Paciente no encontrado</p>
          <button
            onClick={() => navigate('/medico-vfp/pacientes')}
            className="text-purple-600 hover:text-purple-800"
          >
            ← Volver a Pacientes VFP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white print:block print:h-auto print:overflow-visible">
      {/* Print styles scoped to this page: force landscape and adjust page margins */}
      <style>{`
        @media print {
          @page { 
            size: letter portrait;
            margin: 2cm 1.5cm;
          }
          body {
            width: 21.59cm;
            max-width: 21.59cm;
          }
        }
      `}</style>
      {/* Header */}
      <div className="bg-white shadow print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/medico-vfp/pacientes')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Volver
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Historial Clínico</h1>
                <p className="text-gray-600">
                  {paciente.apellido1} {paciente.apellido2} {paciente.nombre}
                </p>
              </div>
            </div>
            <div className="flex gap-3 print:hidden">
              {!editing ? (
                <>
                  <button
                    onClick={() => setEditing(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <PrinterIcon className="w-5 h-5" />
                    Imprimir
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setHistorial(paciente.historial || '');
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Guardar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 print:max-w-none print:px-0 print:py-0 print:space-y-4 allow-print-breaks">
        {/* Info del Paciente */}
        <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Datos del Paciente</h2>
              <p className="text-sm text-gray-600">CLAVE: {paciente.clave}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 print:grid-cols-2 print:gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Nombre Completo</label>
              <p className="text-gray-900">
                {paciente.apellido1} {paciente.apellido2} {paciente.nombre}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Fecha de Nacimiento</label>
              <p className="text-gray-900">
                {paciente.nacimiento ? new Date(paciente.nacimiento).toLocaleDateString('es-ES') : '-'}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Edad</label>
              <p className="text-gray-900">{calcularEdad(paciente.nacimiento)}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Sexo</label>
              <p className="text-gray-900">
                {paciente.sexo === 'H' ? 'Hombre' : paciente.sexo === 'M' ? 'Mujer' : '-'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <ScaleIcon className="w-5 h-5 text-gray-400" />
              <div>
                <label className="text-sm font-medium text-gray-700 block">Peso</label>
                <p className="text-gray-900">{paciente.peso ? `${paciente.peso} kg` : '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <RulerIcon className="w-5 h-5 text-gray-400" />
              <div>
                <label className="text-sm font-medium text-gray-700 block">Talla</label>
                <p className="text-gray-900">{paciente.talla ? `${paciente.talla} m` : '-'}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Dirección</label>
              <p className="text-gray-900">{paciente.medicoVfpPaciDat?.direccion || '-'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Teléfono</label>
              <p className="text-gray-900">
                {paciente.medicoVfpPaciDat?.tel1 || paciente.medicoVfpPaciDat?.tel2 || '-'}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Padre/Tutor</label>
              <p className="text-gray-900">{paciente.medicoVfpPaciDat?.padre || '-'}</p>
            </div>
          </div>
        </div>

        {/* Historial Clínico */}
        <div className="bg-white rounded-lg shadow print:shadow-none">
          <div className="p-6 border-b border-gray-200 print:p-4">
            <h2 className="text-lg font-semibold text-gray-900">Historial Clínico</h2>
            <p className="text-sm text-gray-600 mt-1">Registro completo de consultas</p>
          </div>

          <div className="p-6 print:p-4">
            {editing ? (
              <div className="space-y-4">
                <textarea
                  value={historial}
                  onChange={(e) => setHistorial(e.target.value)}
                  rows={20}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Escriba el historial clínico..."
                />
                <p className="text-sm text-gray-500">
                  Caracteres: {historial.length}
                </p>
              </div>
            ) : (
              <div className="prose max-w-none print:prose">
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900 bg-gray-50 p-6 rounded-lg print:bg-white print:p-4 print:text-[10px]">
                  {historial || 'Sin historial registrado'}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
